import { useCallback } from 'react';
import { useFormikContext } from 'formik';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { DraggableList } from '../../../../components/DraggableList';
import { Loading } from '../../../../components/Loading';
import { useFindAllCostsQuery } from '../../../../components/CostList/hooks/useFindAllCostsQuery.hook';
import { useContextualizedFields } from '../../../../hooks/useContextualizedFields.hook';

import type {
  OptionalCostUsageFormContext,
  RequiredCostUsageFormContext,
  CostUsageForm_CostUsage,
} from '../../fragments/cost-usage.fragment';

interface Props {
  readonly formContext?: string;
  readonly id: string;
}

export const CostsOrderFormStep = ({ formContext }: Readonly<Props>) => {
  const { setFieldValue } = useFormikContext<
    OptionalCostUsageFormContext | RequiredCostUsageFormContext
  >();

  const fields = useContextualizedFields<
    Readonly<{
      costUsages: readonly CostUsageForm_CostUsage[] | undefined;
    }>
  >(
    {
      costUsages: {
        id: 'cost-usages-order',
        name: 'costUsages',
      },
    },
    formContext,
  );

  const setItems = useCallback(
    (newOrderedListOfCosts: readonly CostUsageForm_CostUsage[]) => {
      void setFieldValue(fields.costUsages.name, newOrderedListOfCosts);
    },
    [setFieldValue, fields.costUsages.name],
  );

  const { data, isLoading } = useFindAllCostsQuery();

  const getLabel = useCallback(
    (costUsage: Readonly<CostUsageForm_CostUsage>) => {
      const allCosts = data?.findAllCosts ?? EMPTY_ARRAY;

      const resolvedCostUsage = allCosts.find(
        (cost) => cost._id === costUsage.costId,
      );

      return resolvedCostUsage
        ? `${resolvedCostUsage.code} - ${resolvedCostUsage.label}`
        : EMPTY_STRING;
    },
    [data?.findAllCosts],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <DraggableList
      getLabel={getLabel}
      idProp="costId"
      indexProp="index"
      items={fields.costUsages.value ?? EMPTY_ARRAY}
      setItems={setItems}
    />
  );
};
